import { z } from "zod";
import { idSchema, idempotencyKeySchema, instantSchema } from "./common.js";

/**
 * Share-link schemas — Phase 10a values projected at the network
 * boundary. The codec is the canonical encoder; these schemas
 * validate the wire shape on the way in (`share.issue` / `share.revoke`
 * input) and on the way out (`share.resolve` output).
 */

export const shareTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("site"), siteId: idSchema }),
  z.object({ kind: z.literal("panel"), panelId: idSchema }),
]);
export type ShareTargetInput = z.infer<typeof shareTargetSchema>;

export const shareExpirationPolicySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("duration"),
    seconds: z.number().int().positive(),
  }),
  z.object({ kind: z.literal("never") }),
]);
export type ShareExpirationPolicyInput = z.infer<
  typeof shareExpirationPolicySchema
>;

export const shareLinkSchema = z.object({
  id: idSchema,
  token: idSchema,
  target: shareTargetSchema,
  ownerId: idSchema,
  expirationPolicy: shareExpirationPolicySchema,
  createdAt: instantSchema,
  revokedAt: instantSchema.optional(),
});
export type ShareLinkContract = z.infer<typeof shareLinkSchema>;

export const shareIssueInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  target: shareTargetSchema,
  expirationPolicy: shareExpirationPolicySchema,
});
export type ShareIssueInput = z.infer<typeof shareIssueInputSchema>;

export const shareRevokeInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  id: idSchema,
});
export type ShareRevokeInput = z.infer<typeof shareRevokeInputSchema>;

/**
 * Phase 12e — `share.issue` response shape. Carries the persisted
 * `ShareLink` plus the rendered public URL (built via Phase 10c's
 * `buildShareUrl` against `PUBLIC_BASE_URL`). The Phase 13h share
 * confirmation surface reads both fields: the link drives the
 * "expires Friday 5pm" hint, the URL drives the "copy link" affordance.
 */
export const shareIssueOutputSchema = z.object({
  link: shareLinkSchema,
  url: z.string().url(),
});
export type ShareIssueOutput = z.infer<typeof shareIssueOutputSchema>;

/**
 * Phase 12e — `share.listMine` response shape. The Phase 13h share-
 * management surface renders the per-issuer list ordered most-recently
 * issued first (per the `ShareLinkRepository.listByOwner` contract).
 */
export const shareListMineOutputSchema = z.object({
  links: z.array(shareLinkSchema),
});
export type ShareListMineOutput = z.infer<typeof shareListMineOutputSchema>;

export const shareResolveInputSchema = z.object({
  token: idSchema,
});
export type ShareResolveInput = z.infer<typeof shareResolveInputSchema>;
